/**
 * SVG circular countdown timer.
 * Displays arc + seconds, color-coded by urgency.
 */

interface CircularTimerProps {
  readonly remaining: number;
  readonly total: number;
  readonly size?: number;
}

export function CircularTimer({
  remaining,
  total,
  size = 36,
}: CircularTimerProps) {
  const center = size / 2;
  const strokeWidth = 3;
  const radius = center - strokeWidth;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? Math.max(0, remaining / total) : 0;
  const offset = circumference * (1 - progress);

  const isCritical = remaining <= 3;
  const isWarning = remaining <= 5;
  const color = isCritical
    ? "var(--cn-asf-timer-critical)"
    : isWarning
      ? "var(--cn-asf-timer-warning)"
      : "var(--cn-asf-ready)";
  const trackColor = "rgba(0, 0, 0, 0.08)";

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="timer"
      aria-label={`${remaining}초 남음`}
      style={{
        transform: "rotate(-90deg)",
        animation: isCritical
          ? "asf-timer-shake 0.3s ease-in-out infinite"
          : undefined,
      }}
    >
      {/* Track */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={trackColor}
        strokeWidth={strokeWidth}
      />
      {/* Progress arc */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.3s, stroke 0.3s" }}
      />
      {/* Centered text (counter-rotate to stay upright) */}
      <text
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="central"
        fill={color}
        fontSize={size * 0.36}
        fontWeight={700}
        fontFamily="monospace"
        style={{ transform: "rotate(90deg)", transformOrigin: "center" }}
      >
        {remaining}
      </text>
    </svg>
  );
}
